import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {GoldenRecordService} from "../../../Services/goldenRecord.service";
import {LoginService} from "../../../Services/login.service";
import {DxContextMenuComponent} from "devextreme-angular";
import {TranslateService} from "@ngx-translate/core";
import {SystemService} from "../../../Services/system.service";
import {SignalrService} from "../../../Services/signalr.service";
import {CatalogService} from "../../../Services/CatalogManagement/catalog.service";
import {ExportStageService} from "../../../Services/DataProcess/export-stage.service";
import {ImportContainerService} from "../../../Services/DataProcess/import-container.service";
import {custom} from "devextreme/ui/dialog";
import {GoldenRecordRow} from "./golden-record-row.model";

@Component({
    selector: 'app-golden-record-stage',
    templateUrl: './golden-record-stage.component.html',
    styleUrls: ['./golden-record-stage.component.css'],
    standalone: false
})
export class GoldenRecordStageComponent implements OnInit {

  @ViewChild('rowMenu', {static: false})
  rowMenu?: DxContextMenuComponent;

  public catalogs: GoldenRecordRow[] = [];

  public selectedCatalogId: string = '';
  public selectedCatalogName: string = '';
  public selectedCatalogLogo: string = '';
  public backUpVisible: boolean = false;

  public currentMenuItems: any[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private menuRow?: GoldenRecordRow;
  private _loadVersion = 0;
  private logoByCatalogId = new Map<string, string>();

  constructor(
    private readonly router: Router,
    private readonly goldenRecordService: GoldenRecordService,
    public readonly loginService: LoginService,
    private readonly translate: TranslateService,
    private readonly systemService: SystemService,
    private readonly signalrService: SignalrService,
    private readonly catalogService: CatalogService,
    private readonly exportStageService: ExportStageService,
    private readonly importContainerService: ImportContainerService
  ) {
    this.systemService.currentNavigationTitle = this.translate.instant('Golden Record');
  }

  async ngOnInit(): Promise<void> {
    await this.loadStage();

    this.signalrService.refreshCatalogList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadStage());
  }

  private async loadStage(): Promise<void> {
    const customerId = this.loginService.currentCustomer.id;
    const version = ++this._loadVersion;

    try {
      const stage = await this.goldenRecordService.getGoldenRecordStage(customerId);
      await this.catalogService.setCatalogs(customerId);
      await this.loadCatalogLogos(customerId);
      if (version !== this._loadVersion) return;

      const catalogIds = stage?.catalogIds ?? [];

      const rows = await Promise.all(catalogIds.map(async id => {
        const fromList = this.catalogService.catalogs?.find(c => c.id === id);
        const name = fromList?.catalogName ?? await this.catalogService.getCatalogName(id, customerId);
        return {
          id,
          name,
          date: fromList?.date,
          isGoldenRecord: id === stage?.goldenRecordCatalogId
        } as GoldenRecordRow;
      }));

      if (version !== this._loadVersion) return;

      const toTime = (d: any) => d ? new Date(d).getTime() : 0;
      rows.sort((a, b) => toTime(b.date) - toTime(a.date));

      this.catalogs = rows;
      this.syncSelection();
    } catch {
      if (version === this._loadVersion) {
        this.catalogs = [];
        this.selectedCatalogId = '';
        this.selectedCatalogName = '';
      }
    }
  }

  private async loadCatalogLogos(customerId: string): Promise<void> {
    try {
      const containers = await this.importContainerService.getAll(customerId);
      this.logoByCatalogId.clear();
      for (const container of containers ?? []) {
        if (!container.logoLink) continue;
        for (const catalogId of container.catalogIds ?? []) {
          this.logoByCatalogId.set(catalogId, container.logoLink);
        }
      }
    } catch {
      this.logoByCatalogId.clear();
    }
  }

  private syncSelection(): void {
    if (this.selectedCatalogId && this.catalogs.some(c => c.id === this.selectedCatalogId)) {
      const stillThere = this.catalogs.find(c => c.id === this.selectedCatalogId);
      this.selectedCatalogName = stillThere?.name ?? '';
      this.selectedCatalogLogo = stillThere ? (this.logoByCatalogId.get(stillThere.id) ?? '') : '';
      return;
    }

    const goldenRecord = this.catalogs.find(c => c.isGoldenRecord) ?? this.catalogs[0];
    this.selectedCatalogId = goldenRecord?.id ?? '';
    this.selectedCatalogName = goldenRecord?.name ?? '';
    this.selectedCatalogLogo = goldenRecord ? (this.logoByCatalogId.get(goldenRecord.id) ?? '') : '';
  }

  onRowClick(row: GoldenRecordRow): void {
    this.selectedCatalogId = row.id;
    this.selectedCatalogName = row.name;
    this.selectedCatalogLogo = this.logoByCatalogId.get(row.id) ?? '';
  }

  openCatalog(catalogId: string): void {
    const customerId = this.loginService.currentCustomer.id;
    this.router.navigate([
      '/dataprocess/golden-record/catalog',
      customerId,
      catalogId,
      'infos'
    ]);
  }

  async onCrownClick(row: GoldenRecordRow, event: Event): Promise<void> {
    event.stopPropagation();

    if (row.isGoldenRecord) {
      this.systemService.notifyInfo(this.translate.instant('Dieser Katalog ist bereits der Golden Record.'));
      return;
    }

    const confirmDialog = custom({
      title: this.translate.instant('Golden Record festlegen?'),
      messageHtml: `${this.translate.instant('Wollen Sie wirklich diesen Katalog als Golden Record promoten?')}<br/><br/><b>${row.name}</b><br/><br/>${this.translate.instant('Ein bisheriger Golden Record verliert dadurch diesen Status.')}`,
      buttons: [
        {text: this.translate.instant('Ja'), onClick: () => true},
        {text: this.translate.instant('Nein'), onClick: () => false}
      ]
    });

    if (!await confirmDialog.show())
      return;

    const customerId = this.loginService.currentCustomer.id;
    try {
      await this.goldenRecordService.setGoldenRecord(row.id, customerId);
      this.systemService.notifyInfo(this.translate.instant('Golden Record wurde gesetzt.'));
      await this.loadStage();
    } catch {
      this.systemService.notifyInfo(this.translate.instant('Fehler beim Setzen des Golden Records.'));
    }
  }

  openRowMenu(e: any, row: GoldenRecordRow): void {
    this.menuRow = row;
    this.currentMenuItems = [
      {id: 'open', text: this.translate.instant('Katalog öffnen')},
      {id: 'duplicate', text: this.translate.instant('Katalog duplizieren')},
      {id: 'copy', text: this.translate.instant('Katalog kopieren')},
      ...(this.catalogService.copyCatalogId ? [{id: 'paste', text: this.translate.instant('Katalog einfügen')}] : []),
      {id: 'restorePoint', text: this.translate.instant('Wiederherstellungspunkt erstellen')},
      {id: 'backup', text: this.translate.instant('Backups anzeigen')},
      {id: 'moveToExport', text: this.translate.instant('Verschieben in Exportstage')},
      {id: 'delete', text: this.translate.instant('Löschen')},
    ];

    const targetElement = e.element as HTMLElement;
    this.rowMenu?.instance.option('target', targetElement);
    this.rowMenu?.instance.option('dataSource', this.currentMenuItems);
    this.rowMenu?.instance.show();
  }

  async onMenuItemClick(event: any): Promise<void> {
    const row = this.menuRow;
    if (!row) return;

    switch (event.itemData.id) {
      case 'open':
        this.openCatalog(row.id);
        break;
      case 'duplicate':
        this.onDuplicate(row);
        break;
      case 'copy':
        this.onCopy(row);
        break;
      case 'paste':
        this.onPaste();
        break;
      case 'restorePoint':
        this.onCreateRestorePoint(row);
        break;
      case 'backup':
        this.backUpVisible = true;
        break;
      case 'moveToExport':
        this.onMoveToExport(row);
        break;
      case 'delete':
        await this.onDelete(row);
        break;
    }
  }

  private onDuplicate(row: GoldenRecordRow): void {
    this.systemService.notifyInfo(this.translate.instant('Der Katalog wird dupliziert'));
    this.catalogService.duplicateCatalog(row.id);
  }

  private onCopy(row: GoldenRecordRow): void {
    this.catalogService.copyCatalogId = row.id;
    this.catalogService.copyCatalogCustomerId = this.loginService.currentCustomer.id;
    this.systemService.notifyInfo(this.translate.instant('Katalog kopiert. Über "Katalog einfügen" einfügen.'));
  }

  private onPaste(): void {
    if (!this.catalogService.copyCatalogId) return;
    this.systemService.notifyInfo(this.translate.instant('Katalog wird eingefügt'));
    this.catalogService.duplicateCatalog(this.catalogService.copyCatalogId, this.catalogService.copyCatalogCustomerId);
  }

  private onCreateRestorePoint(row: GoldenRecordRow): void {
    this.systemService.notifyInfo(this.translate.instant('Ein Wiederherstellungspunkt wird per Job erzeugt'));
    this.catalogService.createRestorePoint(row.id);
  }

  private async onMoveToExport(row: GoldenRecordRow): Promise<void> {
    if (!row) return;

    const customerId = this.loginService.currentCustomer.id;

    const goldenRecordWarning = row.isGoldenRecord
      ? `<br/><br/><b>${this.translate.instant('Achtung: Dieser Katalog ist aktuell der Golden Record und verliert dadurch diesen Status.')}</b>`
      : '';

    const confirmDialog = custom({
      title: this.translate.instant('In Exportstage verschieben?'),
      messageHtml: `${this.translate.instant('Soll dieser Katalog wirklich in die Exportstage verschoben werden? Er wird dabei aus der Golden-Record-Stage entfernt.')}<br/><br/><b>${row.name}</b>${goldenRecordWarning}`,
      buttons: [
        {text: this.translate.instant('Ja'), onClick: () => true},
        {text: this.translate.instant('Nein'), onClick: () => false}
      ]
    });

    if (!await confirmDialog.show())
      return;

    try {
      await this.exportStageService.addCatalogToStage(row.id, customerId);
      await this.goldenRecordService.removeCatalogFromStage(row.id, customerId);
      this.systemService.notifyInfo(this.translate.instant('Katalog in die Exportstage verschoben.'));
      if (this.selectedCatalogId === row.id) {
        this.selectedCatalogId = '';
        this.selectedCatalogName = '';
      }
      await this.loadStage();
    } catch {
      this.systemService.notifyInfo(this.translate.instant('Fehler beim Verschieben in die Exportstage.'));
    }
  }

  private async onDelete(row: GoldenRecordRow): Promise<void> {
    const confirmDialog = custom({
      title: this.translate.instant('Wirklich löschen?'),
      messageHtml: `${this.translate.instant('Wollen Sie diesen Katalog wirklich löschen?')}<br/><br/><b>${row.name}</b>`,
      buttons: [
        {text: this.translate.instant('Ja'), onClick: () => true},
        {text: this.translate.instant('Nein'), onClick: () => false}
      ]
    });

    if (!await confirmDialog.show())
      return;

    const customerId = this.loginService.currentCustomer.id;
    try {
      this.catalogService.remCatalog(row.id);
      await this.goldenRecordService.removeCatalogFromStage(row.id, customerId);
      this.systemService.notifyInfo(this.translate.instant('Katalog wird gelöscht'));
      if (this.selectedCatalogId === row.id) {
        this.selectedCatalogId = '';
        this.selectedCatalogName = '';
      }
      await this.loadStage();
    } catch {
      this.systemService.notifyInfo(this.translate.instant('Fehler beim Löschen des Katalogs'));
    }
  }
}
